import {
  ActionCreatorWithoutPayload,
  ActionCreatorWithPayload,
  createListenerMiddleware,
  ListenerEffectAPI,
  TypedStartListening,
  UnknownAction,
  UnsubscribeListener
} from '@reduxjs/toolkit';
import { AppDispatch, RootState } from './createReduxStore';

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export const listenerMiddleware = createListenerMiddleware();
const addListener = listenerMiddleware.startListening as AppStartListening;

const activeListeners: UnsubscribeListener[] = [];
let listenersInitialized = false;

export const setListenersInitialized = (value: boolean) => {
  listenersInitialized = value;
};

export const getListenersInitialized = () => listenersInitialized;

type ListenerEffect = (
  action: UnknownAction,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>
) => void | Promise<void>;

type ActionCreatorType =
  | ActionCreatorWithPayload<any, string>
  | ActionCreatorWithoutPayload<string>
  | ((...args: any[]) => { type: string })
  | string;

const hasMatchMethod = (
  actionCreator: ActionCreatorType
): actionCreator is ActionCreatorWithPayload<any, string> | ActionCreatorWithoutPayload<string> => {
  return typeof (actionCreator as any).match === 'function';
};

export const createListener = (
  actionCreators: ActionCreatorType[],
  effect: ListenerEffect,
  options: {
    timeout?: number;
    cancelWhen?: (state: RootState) => boolean;
  } = {}
) => {
  if (listenersInitialized) {
    return;
  }

  const unsubscribe = addListener({
    matcher: (action): action is UnknownAction => {
      return actionCreators.some((actionCreator) => {
        if (typeof actionCreator === 'string') {
          return action.type === actionCreator;
        } else if (typeof actionCreator === 'function') {
          if (hasMatchMethod(actionCreator)) {
            return actionCreator.match(action);
          } else {
            return actionCreator().type === action.type;
          }
        } else {
          return false;
        }
      });
    },
    effect: async (action, listenerApi) => {
      // Check if listener should be cancelled based on state
      if (options.cancelWhen && options.cancelWhen(listenerApi.getState())) {
        return;
      }

      // Implement timeout protection to prevent hanging async operations
      const timeoutPromise = options.timeout
        ? new Promise((_, reject) => setTimeout(() => reject(new Error('Listener timeout')), options.timeout))
        : null;

      try {
        if (timeoutPromise) {
          await Promise.race([effect(action, listenerApi), timeoutPromise]);
        } else {
          await effect(action, listenerApi);
        }
      } catch (error) {
        console.error('Listener error:', error);
      }
    }
  });

  activeListeners.push(unsubscribe);
  return unsubscribe;
};
